import React from 'react';
import {Dot} from '@/map/components/Overlay';
import {palette} from '@/styles';
import {Marker, Polyline} from '../../Overlay';
import {RulerInfoCursorOptions, RulerItem} from './index.d';
import {InfoCursor} from './InfoCursor';
import {MileStone} from './MileStone';

interface IProps {
  map: naver.maps.Map;
  item: RulerItem & {
    id: number;
  };
  borderColor?: string;
  strokeWeight?: number;
  infoOptions?: RulerInfoCursorOptions;
  removeLayer?(layerId?: number): void;
}

const COLOR = palette.teal200;

export const RulerOverlay = React.memo(
  ({
    map,
    item,
    borderColor = COLOR,
    strokeWeight = 4,
    infoOptions,
    removeLayer,
  }: IProps) => {
    const accDistance = React.useMemo(() => {
      if (!item || !map) {
        return;
      } else if (item?.mileStons?.length) {
        return item.mileStons.reduce((acc, cur) => (acc += cur.distance), 0);
      } else {
        return item.path.reduce((acc, cur, index) => {
          if (index > 0) {
            const distance = map
              .getProjection()
              .getDistance(item.path[index - 1], cur);
            acc += distance;
          }

          return acc;
        }, 0);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [item?.mileStons, map]);

    if (!item || !map) {
      return null;
    }

    return (
      <>
        {item.path?.length > 0 && (
          <>
            <Polyline
              map={map}
              path={item.path}
              clickable={false}
              strokeColor={borderColor}
              strokeWeight={strokeWeight}
            />
            <Marker
              map={map}
              position={item.path[0]}
              draggable={false}
              clickable={false}
            >
              <Dot borderWidth={4} borderColor={borderColor} />
            </Marker>
            {item.id > 0 && (
              <>
                <Marker
                  map={map}
                  position={item.path[item.path.length - 1]}
                  draggable={false}
                  clickable={false}
                >
                  <Dot borderWidth={4} borderColor={borderColor} />
                </Marker>
                <InfoCursor
                  map={map}
                  distance={accDistance}
                  position={item.path[item.path.length - 1]}
                  id={item.id}
                  options={infoOptions}
                  onRemove={removeLayer}
                />
              </>
            )}
          </>
        )}
        {!!item.mileStons?.length &&
          item?.mileStons
            .slice(0, item.mileStons.length - (item.id > 0 ? 1 : 0))
            .map((i) => (
              <MileStone
                map={map}
                key={JSON.stringify(i.coord)}
                borderColor={borderColor}
                position={i.coord}
                distance={i.distance}
                draggable={false}
                clickable={false}
              />
            ))}
      </>
    );
  }
);
